import React, { useEffect, useMemo, useRef, useState } from 'react';
import useProfessionOptions from '../hooks/useProfessionOptions';
import { useAuth } from '../contexts/AuthContext';
import {
  ALL_PROFESSION,
  normalizeProfessionName,
  canonicalizeProfessionName,
  professionKey,
} from '../utils/professions';

/** aalogin app admins only — not franchisee / general vyapar */
const APP_ADMIN_ROLES = ['app_admin', 'super_admin'];

/**
 * Optional profession text box with list-based autocomplete (not browser cache).
 * - Empty => ALL (optional)
 * - Type "st" / "job" => suggestions from shared list
 * - Space-variants deduped (JOB SEEKER === JOBSEEKER)
 * - Not in list => Other automatically
 * - Add / Delete only for app_admin / super_admin (aalogin)
 */
export default function ProfessionSelectField({
  value = '',
  onChange,
  className = 'smart-bill-qr-form-control',
  label = 'Profession',
  labelClassName = 'smart-bill-qr-form-label',
  optional = true,
  showLabel = true,
  id = 'customer-profession',
  allowManageList,
}) {
  const { user } = useAuth();
  const canManageList =
    typeof allowManageList === 'boolean'
      ? allowManageList
      : APP_ADMIN_ROLES.includes(user?.role);

  const {
    options,
    loading,
    adding,
    deleting,
    addProfession,
    deleteProfession,
    canDeleteProfession,
  } = useProfessionOptions();

  const [inputText, setInputText] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const listId = `${id}-suggestions`;

  useEffect(() => {
    const normalized = canonicalizeProfessionName(value);
    if (!normalized || normalized === ALL_PROFESSION) {
      setInputText('');
    } else {
      setInputText(normalized);
    }
  }, [value]);

  const suggestions = useMemo(() => {
    const q = normalizeProfessionName(inputText);
    const qKey = professionKey(q);
    if (!q) return options.slice(0, 12);
    return options
      .filter((p) => p.includes(q) || professionKey(p).includes(qKey))
      .slice(0, 12);
  }, [inputText, options]);

  const exactMatch = useMemo(() => {
    const q = normalizeProfessionName(inputText);
    if (!q) return null;
    const qKey = professionKey(q);
    return (
      options.find((p) => professionKey(p) === qKey) ||
      null
    );
  }, [inputText, options]);

  const isOther =
    Boolean(normalizeProfessionName(inputText)) && !exactMatch;

  const emit = (raw) => {
    const normalized = canonicalizeProfessionName(raw);
    const next =
      !normalized || normalized === ALL_PROFESSION ? '' : normalized;
    if (typeof onChange === 'function') onChange(next);
  };

  const pick = (name) => {
    const normalized = canonicalizeProfessionName(name);
    setInputText(normalized);
    setOpen(false);
    setHighlight(0);
    emit(normalized);
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    setInputText(raw);
    setOpen(true);
    setHighlight(0);
    // While typing, if it matches a list key, emit canonical form
    const match = options.find(
      (p) => professionKey(p) === professionKey(raw),
    );
    emit(match || raw);
  };

  const handleFocus = () => setOpen(true);

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 150);
    const match = options.find(
      (p) => professionKey(p) === professionKey(inputText),
    );
    const normalized = match || canonicalizeProfessionName(inputText);
    setInputText(normalized === ALL_PROFESSION ? '' : normalized);
    emit(normalized === ALL_PROFESSION ? '' : normalized);
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }
    if (!open) {
      if (e.key === 'Escape') {
        setInputText('');
        emit('');
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) =>
        suggestions.length ? (h + 1) % suggestions.length : 0,
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) =>
        suggestions.length
          ? (h - 1 + suggestions.length) % suggestions.length
          : 0,
      );
    } else if (e.key === 'Enter') {
      if (suggestions[highlight]) {
        e.preventDefault();
        pick(suggestions[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleAddToList = async () => {
    const created = await addProfession(inputText);
    if (created) {
      setInputText(created);
      emit(created);
      setOpen(false);
    }
  };

  const handleDelete = async (name, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      !window.confirm(
        `Delete "${name}" from the profession list?\n(Duplicate spellings like "JOB SEEKER" will also be removed.)`,
      )
    ) {
      return;
    }
    const ok = await deleteProfession(name);
    if (ok && professionKey(inputText) === professionKey(name)) {
      setInputText('');
      emit('');
    }
  };

  useEffect(() => {
    const onDoc = (ev) => {
      if (wrapRef.current && !wrapRef.current.contains(ev.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div
      className="profession-select-field"
      ref={wrapRef}
      style={{ position: 'relative' }}
    >
      {showLabel && (
        <label className={labelClassName} htmlFor={id}>
          {label}
          {optional ? '' : ' *'}
        </label>
      )}

      <input
        id={id}
        type="text"
        className={className}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="characters"
        spellCheck={false}
        placeholder="ALL (optional) — type to search list"
        value={inputText}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={loading}
      />

      {open && (suggestions.length > 0 || !inputText) && (
        <ul
          id={listId}
          role="listbox"
          style={{
            position: 'absolute',
            zIndex: 30,
            left: 0,
            right: 0,
            top: '100%',
            margin: 0,
            padding: '4px 0',
            listStyle: 'none',
            background: '#fff',
            border: '1px solid #c5d0dc',
            borderRadius: 6,
            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          <li
            role="option"
            aria-selected={!inputText}
            onMouseDown={(e) => {
              e.preventDefault();
              pick('');
            }}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: 13,
              color: '#555',
              background: !inputText ? '#eef5ff' : 'transparent',
            }}
          >
            ALL (optional)
          </li>
          {suggestions.map((p, idx) => (
            <li
              key={p}
              role="option"
              aria-selected={highlight === idx}
              onMouseDown={(e) => {
                // Don't pick when clicking delete
                if (e.target.closest?.('[data-profession-delete]')) return;
                e.preventDefault();
                pick(p);
              }}
              onMouseEnter={() => setHighlight(idx)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                background: highlight === idx ? '#e3f2fd' : 'transparent',
                color: '#1a1a1a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <span>{p}</span>
              {canManageList && canDeleteProfession(p) && (
                <button
                  type="button"
                  data-profession-delete="1"
                  title="Delete from list (app admin)"
                  disabled={deleting}
                  onMouseDown={(e) => handleDelete(p, e)}
                  style={{
                    border: '1px solid #dc2626',
                    background: '#fff',
                    color: '#dc2626',
                    borderRadius: 4,
                    fontSize: 11,
                    padding: '2px 8px',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  Delete
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div
        style={{
          marginTop: 6,
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          minHeight: 22,
        }}
      >
        {!inputText && (
          <span style={{ fontSize: 11, color: '#6b7280' }}>
            Default: ALL professions (optional)
          </span>
        )}
        {exactMatch && (
          <span style={{ fontSize: 11, color: '#15803d' }}>
            Matched from list: {exactMatch}
          </span>
        )}
        {isOther && (
          <span style={{ fontSize: 11, color: '#b45309' }}>
            Other (not in list) — will save as typed
          </span>
        )}
        {isOther && canManageList && (
          <button
            type="button"
            disabled={adding || !normalizeProfessionName(inputText)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleAddToList}
            style={{
              fontSize: 12,
              padding: '3px 10px',
              border: '1px solid #1976d2',
              background: '#fff',
              color: '#1976d2',
              borderRadius: 4,
              cursor: 'pointer',
              opacity: adding ? 0.6 : 1,
            }}
          >
            {adding ? 'Adding…' : '+ Add to list'}
          </button>
        )}
        {loading && (
          <span style={{ fontSize: 11, color: '#888' }}>Loading list…</span>
        )}
      </div>
    </div>
  );
}

export { normalizeProfessionName };
